import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { IconButton, LinearLoader } from '@sc/shared-ui';
import LockUp from '../assets/brand/lock-up.svg';

interface Props {
  children: ReactNode;
  footer?: ReactNode | undefined;
  onBack?: (() => void) | undefined;
  loading?: boolean | undefined;
  showLockUp?: boolean | undefined;
  showPrivacy?: boolean | undefined;
}

export const AuthLayout = ({
  children,
  footer,
  onBack,
  loading,
  showLockUp,
  showPrivacy,
}: Props): ReactElement => (
  <div className="relative h-full w-full flex flex-col overflow-y-auto">
    {loading && (
      <div className="absolute inset-x-0 top-0 z-10">
        <LinearLoader />
      </div>
    )}

    <div className="relative grow w-full max-w-105 mx-auto px-5 py-10 flex flex-col justify-between gap-10">
      <div className="flex flex-col gap-10">
        <div className="flex items-center min-h-12">
          {onBack && (
            <IconButton
              type="light"
              size="large"
              Icon={ArrowLeftIcon}
              tooltip="Back"
              onClick={onBack}
            />
          )}
        </div>

        {showLockUp && <LockUp className="h-5 mx-auto" />}

        {children}
      </div>

      <div className="flex flex-col gap-5">
        {footer}
        {showPrivacy && <AuthPrivacyNotice />}
      </div>
    </div>
  </div>
);

export const AuthTitle = ({
  children,
  subtitle,
}: {
  children: ReactNode;
  subtitle?: ReactNode | undefined;
}): ReactElement => (
  <div className="w-full text-center">
    <h1 className="font-grotesque font-semibold text-h1 text-white">
      {children}
    </h1>
    {subtitle && (
      <p className="text-text-low-priority text-base leading-6 mt-4">
        {subtitle}
      </p>
    )}
  </div>
);

export const AuthAnnouncement = ({
  Icon,
  title,
  children,
}: {
  Icon: HeroIconType;
  title: string;
  children: ReactNode;
}): ReactElement => (
  <div className="w-full flex flex-col items-center text-center">
    <div className="pt-5 pb-4">
      <div className="p-6 rounded-lg bg-primary-t-1000">
        <Icon className="size-11 text-primary-300" />
      </div>
    </div>
    <h1 className="font-grotesque font-semibold text-h1-small text-white mb-4">
      {title}
    </h1>
    {/* the layout's gap-10 supplies the rest */}
    <div className="text-base leading-6 text-text-low-priority mb-8">
      {children}
    </div>
  </div>
);

export const AuthSupportLink = (): ReactElement => (
  <p className="text-center text-body-small text-text-low-priority">
    Still having trouble? Contact{' '}
    <a className="underline text-primary-400" href="mailto:support@starkchat.com">
      support@starkchat.com
    </a>
    .
  </p>
);

export const AuthPrivacyNotice = (): ReactElement => (
  <p className="text-text-low-priority text-center text-sm">
    By continuing, you acknowledge Stark Chat&apos;s{' '}
    <a
      className="underline text-primary-400"
      href="https://starkchat.com/privacy"
      target="_blank"
      rel="noreferrer noopener"
    >
      Privacy Policy
    </a>
    .
  </p>
);

// 60s matches the API's cooldownSeconds, so it unlocks when a resend would work
export const useResendCountdown = (seconds = 60) => {
  const [remaining, setRemaining] = useState(seconds);

  const countdownRef = useRef<ReturnType<typeof setInterval>>(null);

  const isCountingDown = remaining > 0;

  useEffect(() => {
    if (!isCountingDown) return;

    countdownRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [isCountingDown]);

  return { remaining, isCountingDown, restart: () => setRemaining(seconds) };
};
